import { useQuery } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { queryKeys } from './queries/queryKeys'
import { FeaturePermission } from '../types/types'

// Types for the hook
interface FeaturePermissionOptions {
   showErrors?: boolean
}

interface UseFeaturePermissionsReturn {
   featurePermissions: FeaturePermission
   hasPermission: (
      permission: string,
      user_role: string,
      superAdminValue?: boolean,
   ) => boolean
   isLoading: boolean
   refreshPermissions: () => Promise<void>
}

// Empty default permissions object
const DEFAULT_PERMISSIONS: FeaturePermission = {}

/**
 * Custom hook for feature permissions using React Query
 *
 * @param options - Configuration options
 * @returns Feature permission utilities
 */
const useFeaturePermissions = (
   options: FeaturePermissionOptions = {},
): UseFeaturePermissionsReturn => {
   const { showErrors = true } = options
   const host_url = process.env.REACT_APP_HOST_URL

   // Use React Query to fetch and cache feature permissions
   const { data, isLoading, refetch } = useQuery({
      queryKey: queryKeys.featurePermissions,
      queryFn: async () => {
         try {
            const response = await fetch(`${host_url}/api/feature-permissions`)

            if (!response.ok) {
               throw new Error('Network response was not ok')
            }

            const data = await response.json()
            return data || DEFAULT_PERMISSIONS
         } catch (error) {
            if (showErrors) {
               const errorMessage =
                  error instanceof Error
                     ? error.message
                     : 'Unable to fetch feature permissions.'
               console.error('Feature permissions error:', errorMessage)
               toast.error(`${errorMessage} Using default permissions.`, {
                  duration: 5000,
               })
            }
            return DEFAULT_PERMISSIONS
         }
      },
      enabled: !!host_url,
      staleTime: 60 * 60 * 1000, // 1 hour or until the page is refreshed
      refetchOnWindowFocus: false, // Disable aggressive refetching
      refetchOnReconnect: false, // Disable refetch on reconnect
      retry: 1,
   })

   // Get the current feature permissions
   const featurePermissions = data || DEFAULT_PERMISSIONS

   // Check if a user role has a specific permission
   const hasPermission = (
      permission: string,
      user_role: string,
      superAdminValue: boolean = true,
   ): boolean => {
      // If no permissions loaded yet or no user role provided, deny access
      if (!permission) {
         console.warn('Permission check failed: No permission specified')
         return false
      }

      if (!user_role) {
         console.warn(
            `Permission check failed: No user role specified for permission "${permission}"`,
         )
         return false
      }
      //Super admins should always have permission unless superAdminValue is set to false
      if (user_role === 'Super Admin' && superAdminValue) {
         return superAdminValue
      }

      if (!featurePermissions || Object.keys(featurePermissions).length === 0) {
         console.warn(
            `Permission check with empty permissions: "${permission}" for role "${user_role}"`,
         )
         return false
      }

      // Check if the permission exists and if the user role is included
      const hasAccess =
         featurePermissions[permission]?.includes(user_role) || false
      console.log(
         `Permission check: "${permission}" for role "${user_role}": ${hasAccess ? 'granted' : 'denied'}`,
      )
      return hasAccess
   }

   return {
      featurePermissions,
      hasPermission,
      isLoading,
      refreshPermissions: async () => {
         await refetch()
      },
   }
}

export default useFeaturePermissions
